import { motion } from 'motion/react'
import { cn } from '@/utils'

/**
 * AnimatedPlate: SVG animado de un plato de comida con vapor
 * @param {Object} props
 * @param {number} props.size - Tamaño del SVG en píxeles
 */
function AnimatedPlate({ size = 160 }) {
  return (
    <motion.svg
      width={size}
      height={size}
      viewBox='0 0 64 64'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      role='img'
      aria-label='Plato de comida animado con vapor'
      initial={{ scale: 0.9, opacity: 0, y: 0 }}
      animate={{ scale: 1, opacity: 1, y: [0, -18, 0] }}
      transition={{
        scale: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
        opacity: { duration: 0.6 },
        y: { duration: 2.8, repeat: Infinity, repeatType: 'loop', ease: 'easeInOut' },
      }}
      style={{ display: 'block', margin: '0 auto' }}
    >
      {/* Plato */}
      <ellipse
        cx={32}
        cy={40}
        rx={24}
        ry={10}
        fill='#fff'
        fillOpacity={0.85}
        stroke='#b6e3e9'
        strokeWidth={2.5}
        filter='url(#shadow)'
      />
      {/* Comida animada (sube/baja) */}
      <motion.ellipse
        cx={32}
        cy={40}
        rx={13}
        ry={5.5}
        fill='#fbbf24'
        fillOpacity={0.95}
        initial={{ cy: 40 }}
        animate={{ cy: [40, 38.5, 40] }}
        transition={{ duration: 2.2, repeat: Infinity, repeatType: 'loop', ease: 'easeInOut' }}
      />
      {/* Detalle verde (pulsa) */}
      <motion.ellipse
        cx={38}
        cy={41.5}
        rx={3.5}
        ry={1.2}
        fill='#22c55e'
        fillOpacity={0.7}
        initial={{ rx: 3.5, fillOpacity: 0.7 }}
        animate={{ rx: [3.5, 4.2, 3.5], fillOpacity: [0.7, 1, 0.7] }}
        transition={{
          duration: 1.6,
          repeat: Infinity,
          repeatType: 'mirror',
          ease: 'easeInOut',
          delay: 0.2,
        }}
      />
      {/* Detalle rojo (pulsa) */}
      <motion.ellipse
        cx={27}
        cy={39.5}
        rx={2.2}
        ry={0.8}
        fill='#ef4444'
        fillOpacity={0.7}
        initial={{ rx: 2.2, fillOpacity: 0.7 }}
        animate={{ rx: [2.2, 3, 2.2], fillOpacity: [0.7, 1, 0.7] }}
        transition={{
          duration: 1.3,
          repeat: Infinity,
          repeatType: 'mirror',
          ease: 'easeInOut',
          delay: 0.5,
        }}
      />
      {/* Vapor animado con movimiento ondulante */}
      <motion.path
        d='M28 32 Q30 28 32 32 Q34 36 36 32'
        stroke='#bae6fd'
        strokeWidth='1.2'
        fill='none'
        animate={{
          pathLength: [0.7, 1, 0.7],
          opacity: [0.7, 1, 0.7],
          y: [0, -4, 0],
        }}
        transition={{
          duration: 2.2,
          repeat: Infinity,
          repeatType: 'loop',
          ease: 'easeInOut',
          delay: 0.2,
        }}
      />
      <motion.path
        d='M34 29 Q35 26 36 29 Q37 32 38 29'
        stroke='#bae6fd'
        strokeWidth='1'
        fill='none'
        animate={{
          pathLength: [0.7, 1, 0.7],
          opacity: [0.7, 1, 0.7],
          y: [0, -3, 0],
        }}
        transition={{
          duration: 2.6,
          repeat: Infinity,
          repeatType: 'loop',
          ease: 'easeInOut',
          delay: 0.5,
        }}
      />
      <defs>
        <filter id='shadow' x='0' y='30' width='64' height='20' filterUnits='userSpaceOnUse'>
          <feDropShadow dx='0' dy='2' stdDeviation='2' floodColor='#bae6fd' floodOpacity='0.18' />
        </filter>
      </defs>
    </motion.svg>
  )
}

/**
 * Componente de fallback mientras se carga ARScene
 * @component
 * @description
 * Muestra un plato animado y mensajes de carga mientras se cargan las librerías pesadas
 * de MindAR y Three.js mediante lazy loading.
 *
 * @returns {JSX.Element} Pantalla de carga animada
 */
function ARLoadingFallback() {
  return (
    <motion.div
      className={cn('flex min-h-screen items-center justify-center bg-white')}
      role='status'
      aria-live='polite'
      aria-busy='true'
      aria-label='Buscando marcador AR...'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <motion.div className={cn('h-2 w-64 overflow-hidden rounded-full bg-gray-200 shadow-inner')}>
        <AnimatedPlate />
      </motion.div>
      <span className='sr-only'>Buscando marcador AR...</span>
    </motion.div>
  )
}
export { ARLoadingFallback }
